import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { CircleAlert, CircleCheck, ExternalLink, LoaderCircle, Wand2 } from "lucide-react"
import { useEffect, useState } from "preact/hooks"
import {
  CUSTOM_WIZ_MAX_COMBINED,
  CustomWizContextOptions,
  CustomWizMode,
  DEFAULT_CONTEXT_OPTIONS,
  RunCustomResultItem,
  build_run_custom_body,
  run_custom_wiz,
  validate_custom_wiz_input,
} from "@/api/custom_wiz"
import { addBigWizInProgress, removeBigWizInProgress } from "@/deduction_table/triage_state"
import { toast } from "@/components/ui/use-toast"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"
import { classes } from "@/utils"

export interface CustomWizDeduction {
  id: string
  invoice_number: string
}

interface CustomWizDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  deductions: CustomWizDeduction[]
  /** Called when the dialog closes after a completed run (reset selection etc.). */
  onComplete: () => void
}

const CONTEXT_TOGGLES: { key: keyof CustomWizContextOptions; label: string; hint: string }[] = [
  { key: "backup_files", label: "Backup files", hint: "distributor backup PDF + parsed line items" },
  { key: "user_files", label: "Uploaded files", hint: "files your team uploaded" },
  { key: "messages", label: "Emails & messages", hint: "email thread + related emails" },
  { key: "notes", label: "Notes", hint: "internal notes" },
  { key: "checklist", label: "Checklist", hint: "checklist state" },
  { key: "wins_examples", label: "Past wins", hint: "similar successful disputes" },
]

export function CustomWizDialog({ open, onOpenChange, deductions, onComplete }: CustomWizDialogProps) {
  const [prompt, setPrompt] = useState("")
  const [contextOptions, setContextOptions] = useState<CustomWizContextOptions>({ ...DEFAULT_CONTEXT_OPTIONS })
  const [mode, setMode] = useState<CustomWizMode>("per_deduction")
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<RunCustomResultItem[] | null>(null)

  const count = deductions.length
  const combinedDisabled = count > CUSTOM_WIZ_MAX_COMBINED
  const validationError = validate_custom_wiz_input(count, prompt, mode)

  function handleOpenChange(next: boolean) {
    if (loading) return
    onOpenChange(next)
    if (!next && results) {
      setResults(null)
      setPrompt("")
      onComplete()
    }
  }

  function toggleContext(key: keyof CustomWizContextOptions) {
    setContextOptions(prev => ({ ...prev, [key]: !prev[key] }))
  }

  async function handleRunCustomWiz() {
    if (loading || validationError) return
    const ids = deductions.map((d: CustomWizDeduction) => d.id)
    // One key per click so a resubmitted/retried request is a server-side no-op.
    const idempotency_key = crypto.randomUUID()
    setLoading(true)
    addBigWizInProgress(ids)
    try {
      const res = await run_custom_wiz(build_run_custom_body(ids, prompt, contextOptions, mode, idempotency_key))
      if (!res.ok) {
        toast({
          title: "CustomWiz failed",
          description: res.error.message ?? "Request failed",
          variant: "destructive",
        })
        return
      }
      setResults(res.value.data.results)
      toast({ title: "CustomWiz finished", description: res.value.data.message })
      triggerRefreshFlag()
    } finally {
      removeBigWizInProgress(ids)
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-violet-600" aria-hidden />
            CustomWiz
          </DialogTitle>
          <DialogDescription>
            Tell the wizard what to do with the {count} selected deduction{count !== 1 ? "s" : ""}. Suggestions are
            recorded for review on each deduction's Wizard panel — nothing is executed until you approve it.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <CustomWizLoading count={count} mode={mode} />
        ) : results ? (
          <CustomWizResults results={results} deductions={deductions} onClose={() => handleOpenChange(false)} />
        ) : (
          <div class="space-y-4">
            <Textarea
              value={prompt}
              onChange={(e: any) => setPrompt(e.target.value)}
              disabled={loading}
              className="min-h-24"
              placeholder={`e.g. "Re-check each category against the backup and validate anything under $100"`}
            />

            <div>
              <Label className="text-sm font-medium">Context to include</Label>
              <div class="grid grid-cols-2 gap-2 mt-2">
                {CONTEXT_TOGGLES.map(({ key, label, hint }) => (
                  <label
                    key={key}
                    class="flex items-start gap-2 rounded-md border p-2 cursor-pointer hover:bg-slate-50">
                    <Checkbox
                      checked={contextOptions[key]}
                      disabled={loading}
                      onCheckedChange={() => toggleContext(key)}
                      className="mt-0.5"
                    />
                    <span class="text-sm leading-tight">
                      {label}
                      <span class="block text-xs text-gray-500">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Run mode</Label>
              <div class="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={loading}
                  onClick={() => setMode("per_deduction")}
                  className={classes(
                    "flex-1",
                    mode === "per_deduction" && "border-violet-500 bg-violet-50 text-violet-900"
                  )}>
                  One run per deduction ({count})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={loading || combinedDisabled}
                  onClick={() => setMode("combined")}
                  className={classes("flex-1", mode === "combined" && "border-violet-500 bg-violet-50 text-violet-900")}>
                  One combined run
                </Button>
              </div>
              <p class="text-xs text-gray-500 mt-1">
                {combinedDisabled
                  ? `Combined mode supports up to ${CUSTOM_WIZ_MAX_COMBINED} deductions.`
                  : mode === "combined"
                    ? "All deductions go into a single analysis. Backup PDF attachments are skipped in combined mode."
                    : "Each deduction gets its own full-context analysis — the highest-quality option."}
              </p>
            </div>

            {validationError && prompt.trim() !== "" && <p class="text-sm text-red-600">{validationError}</p>}

            <Button className="w-full" disabled={!!validationError} onClick={handleRunCustomWiz}>
              <span class="flex items-center gap-2">
                <Wand2 className="w-4 h-4" aria-hidden />
                Run CustomWiz
              </span>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

const LOADING_MESSAGES = [
  "Gathering each deduction's context…",
  "Reading backups, notes, and emails…",
  "Reasoning through your instructions…",
  "Drafting suggested actions…",
]

interface CustomWizLoadingProps {
  count: number
  mode: CustomWizMode
}

function CustomWizLoading({ count, mode }: CustomWizLoadingProps) {
  const [messageIdx, setMessageIdx] = useState(0)
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const messageTimer = setInterval(() => {
      setMessageIdx((i: number) => (i + 1) % LOADING_MESSAGES.length)
    }, 3500)
    const elapsedTimer = setInterval(() => setElapsed((s: number) => s + 1), 1000)
    return () => {
      clearInterval(messageTimer)
      clearInterval(elapsedTimer)
    }
  }, [])

  return (
    <div class="flex flex-col items-center justify-center gap-4 py-10 text-center">
      <span class="inline-flex animate-spin">
        <LoaderCircle className="w-12 h-12 text-violet-500" aria-hidden />
      </span>
      <div class="space-y-1">
        <p class="text-base font-medium text-gray-900 flex items-center justify-center gap-2">
          <Wand2 className="w-4 h-4 text-violet-600 shrink-0" aria-hidden />
          Running CustomWiz on {count} deduction{count !== 1 ? "s" : ""}
        </p>
        <p class="text-sm text-violet-700 transition-opacity">{LOADING_MESSAGES[messageIdx]}</p>
      </div>
      <p class="text-xs text-gray-400">
        {mode === "combined" ? "Combined analysis" : "One run per deduction"} · this can take a minute or two
        {elapsed >= 3 ? ` · ${format_elapsed(elapsed)} elapsed` : ""}
      </p>
    </div>
  )
}

function format_elapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}m ${secs.toString().padStart(2, "0")}s`
}

interface CustomWizResultsProps {
  results: RunCustomResultItem[]
  deductions: CustomWizDeduction[]
  onClose: () => void
}

function CustomWizResults({ results, deductions, onClose }: CustomWizResultsProps) {
  const invoice_by_id = new Map(deductions.map((d: CustomWizDeduction) => [d.id, d.invoice_number]))
  return (
    <div class="space-y-3">
      <div class="max-h-72 overflow-y-auto space-y-1.5">
        {results.map((r: RunCustomResultItem) => (
          <a
            key={r.deduction_id}
            href={`/deduction/${r.deduction_id}`}
            target="_blank"
            rel="noopener noreferrer"
            class="group flex items-start gap-2 rounded-md border p-2 text-sm hover:border-violet-300 hover:bg-violet-50 transition-colors">
            {r.success ? (
              <CircleCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" aria-hidden />
            ) : (
              <CircleAlert className="w-4 h-4 text-red-600 mt-0.5 shrink-0" aria-hidden />
            )}
            <div class="min-w-0 flex-1">
              <span class="font-medium inline-flex items-center gap-1">
                {invoice_by_id.get(r.deduction_id) ?? r.deduction_id}
                <ExternalLink
                  className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-hidden
                />
              </span>
              {r.success ? (
                <span class="block text-xs text-gray-600">Suggested: {r.action_taken?.split(",").join(", ") || "skip"}</span>
              ) : (
                <span class="block text-xs text-red-600 break-words">{r.error ?? "failed"}</span>
              )}
            </div>
          </a>
        ))}
      </div>
      <p class="text-xs text-gray-500">
        Click a deduction to open its detail page in a new tab, then review and apply the suggestions from its Wizard
        panel.
      </p>
      <Button className="w-full" variant="secondary" onClick={onClose}>
        Done
      </Button>
    </div>
  )
}
